import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { BaseModalComponent } from "../base-modal/base-modal.component";
import { SectionCardComponent } from "../section-card/section-card.component";
import { PillButtonComponent } from "../pill-button/pill-button.component";
import {
  EMPTY_INSIGHT_CARD,
  EXPLORE_ACTION,
  INSIGHT_ACTION_ROUTES,
  InsightCard,
} from "../../services/nova-insight.model";

/**
 * "Nova's Insight" modal - the expanded state of the Insight Card feature (FLIP-169).
 * Opened by NovaInsightWidgetComponent on tap; also reachable standalone from the /test
 * playground (falls back to EMPTY_INSIGHT_CARD's copy when no data is provided).
 *
 * Assembled entirely from shared new-UI pieces:
 *   - app-base-modal    - green full-card gradient, mascot custom header, close x
 *   - app-section-card  - the three insight cards ("small" variant)
 *   - app-pill-button   - the optional bottom CTA
 *
 * Open via MatDialog (content-sized):
 *   this.dialog.open(NovaInsightModalComponent, {
 *     data: card,                        // InsightCard
 *     panelClass: "base-modal-panel",
 *     backdropClass: "base-modal-backdrop",
 *   });
 */
@Component({
  selector: "app-nova-insight-modal",
  templateUrl: "./nova-insight-modal.component.html",
  styleUrls: ["./nova-insight-modal.component.scss"],
  standalone: true,
  imports: [CommonModule, BaseModalComponent, SectionCardComponent, PillButtonComponent],
})
export class NovaInsightModalComponent {
  readonly category: string;
  readonly title: string;
  readonly noticed: string;
  readonly matters: string;
  readonly question: string;
  readonly action: string;
  /**
   * The route `action` maps to, or "" when it maps to nothing. The template renders the CTA on
   * this rather than on `action` itself: the label comes from the model, and a label outside
   * INSIGHT_ACTION_ROUTES has nowhere to navigate, so showing it would give the user a button that
   * silently does nothing. The schema now constrains `action` to the known set, but cards
   * generated before that constraint existed are still out there.
   */
  readonly actionRoute: string;
  /**
   * The Learn lesson this card's EXPLORE CTA opens, or "" for none (FLIP-224).
   *
   * Only ever set for the "Explore" action: every other label maps to a fixed route that has no
   * notion of a target, so a target arriving alongside one of those is ignored rather than
   * honoured.
   */
  readonly actionTargetId: string;

  constructor(
    public dialogRef: MatDialogRef<NovaInsightModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: InsightCard | null,
    private router: Router
  ) {
    const card = data ?? EMPTY_INSIGHT_CARD;
    this.category = this.formatCategory(card.category);
    this.title = card.expandedTitle || card.snackbarTitle;
    this.noticed = card.noticed;
    this.matters = card.matters;
    this.question = card.question;
    this.action = card.action ?? "";
    this.actionRoute = (this.action && INSIGHT_ACTION_ROUTES[this.action]) || "";
    this.actionTargetId = this.action === EXPLORE_ACTION ? (card.actionTargetId ?? "").trim() : "";
  }

  /** Humanizes an internal category key for display, e.g. "STAR_CLUSTER" -> "STAR CLUSTER". */
  private formatCategory(category: string): string {
    return (category || "").replace(/_/g, " ");
  }

  /** x button (base-modal's built-in close) dismisses the dialog. */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Bottom CTA - dismiss the dialog, then navigate to the action's target.
   *
   * NAVIGATION HAPPENS AFTER THE CLOSE COMPLETES, and that ordering is load-bearing (FLIP-224).
   * This used to call close() and router.navigate() in the same tick, which left the dialog
   * teardown and the route change overlapping: CDK's BlockScrollStrategy was still enabled
   * (html { overflow: hidden }, see sass/cdk-theme.scss) with the pre-dialog scrollY recorded, so
   * the new page laid out against a locked <html> and both scroll-to-top resets - the router's
   * scrollPositionRestoration and AppComponent's own rAF scrollTo - were spent against it. Roughly
   * 150ms later the close animation finished, OverlayRef.detach() disabled the scroll strategy, and
   * CDK's window.scroll(0, previousScrollY) re-scrolled the already-rendered new page back down to
   * the OLD page's offset. On iOS that left every position:fixed element - the bottom nav and the
   * Nova FAB alike - resolving against the wrong viewport bottom, floating mid-screen.
   *
   * afterClosed() is the right hook rather than a timeout: CDK disables the scroll strategy inside
   * OverlayRef.detach() BEFORE emitting detachments, which is what afterClosed() fires from. By the
   * time this runs the lock is released and the scroll restored, so the navigation and its
   * scroll-to-top land on a clean document.
   */
  onActionClicked(): void {
    if (!this.actionRoute) {
      this.dialogRef.close();
      return;
    }

    this.dialogRef.afterClosed().subscribe(() => {
      this.releaseScrollBlockResidue();
      this.navigateToAction();
    });

    this.dialogRef.close();
  }

  /**
   * Navigates to the CTA's target: the specific Learn lesson where the card named one, the plain
   * action route otherwise.
   *
   * The lesson opens via LearnShellComponent's `lesson` query param rather than a route of its own
   * - the lesson modal has no URL, it is a param on whichever Learn child route is showing. The
   * shell searches every chapter for the id and silently ignores one it cannot find, so a stale
   * target (ids are reassigned on a content reseed, and cards outlive that in the cache) degrades
   * to the Learn homepage on its own, with no check needed here.
   */
  private navigateToAction(): void {
    if (this.actionTargetId) {
      this.router.navigate([this.actionRoute], {
        // src tells the shell how the lesson was reached - see LearnShellComponent.
        queryParams: { lesson: this.actionTargetId, src: "insight" },
      });
      return;
    }

    this.router.navigate([this.actionRoute]);
  }

  /**
   * Belt and braces on the scroll lock: clear anything CDK's BlockScrollStrategy left on <html>,
   * plus any stray inline body overflow, before the route changes.
   *
   * afterClosed() above already guarantees disable() has run, so in the normal case this finds
   * nothing to do. It exists because the displaced-nav bug persisted across later navigations,
   * which means the bad state outlives the routed page and sits on a long-lived element - and a
   * scroll block still applied when the next page lays out is the one piece of that we can assert
   * away cheaply. Note that sass/cdk-theme.scss overrides CDK's position:fixed to position:static
   * here, which makes CDK's inline top/left inert but does NOT stop it writing them, so they are
   * cleared explicitly rather than assumed harmless.
   *
   * Only touches properties the scroll block itself owns. body.style.overflow is inline-only, so
   * clearing it cannot disturb the stylesheet's own overflow rules - it just undoes
   * LearnLessonModalComponent's hand-rolled lock if that ever leaked.
   */
  private releaseScrollBlockResidue(): void {
    const html = document.documentElement;
    if (html) {
      html.classList.remove("cdk-global-scrollblock");
      html.style.top = "";
      html.style.left = "";
    }

    if (document.body) {
      document.body.style.overflow = "";
    }
  }
}
